<template>
  <div class="loadingAnimation" :style="cssVars">
    <svg class="bubbles" v-if="animationType === 'bubbles'" width="60" height="25" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 121 60">
      <circle r="13" class="bubble3">
        <animateMotion
          :dur="`${duration * 0.64}s`"
          repeatCount="indefinite"
          :path="path">
        </animateMotion>
      </circle>
      <circle r="17" class="bubble2">
        <animateMotion
          :dur="`${duration * 0.8}s`"
          repeatCount="indefinite"
          :path="path">
        </animateMotion>
      </circle>
      <circle r="20" class="bubble1">
        <animateMotion
          :dur="`${duration}s`"
          repeatCount="indefinite"
          :path="path">
        </animateMotion>
      </circle>
    </svg>
    <svg class="bar" v-if="animationType === 'bar'" width="57" height="6" viewBox="0 0 57 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path class="barBg" d="M3.16699 2.9939H53.167" stroke-linecap="round"></path>
      <path class="barAnim" d="M3.16699 2.9939H53.167" stroke-linecap="round"></path>
    </svg>
    <svg class="infinity" v-if="animationType === 'infinity'" width="60" height="25" viewBox="0 0 121 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path class="infinityBg" :d="path" stroke-linecap="round"></path>
      <path class="infinityAnim" :d="path" stroke-linecap="square"></path>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue';

type AnimationType = 'bubbles' | 'bar' | 'infinity';

interface Props {
  animationType?: AnimationType;
  duration?: number;
}
const props = withDefaults(defineProps<Props>(), {
  animationType: 'bar',
  duration: 3,
});

const path: string =
  `M61.4504 25.9577L43.815 8.32233C34.0205 -1.44078 18.1404 -1.44078 8.3459 8.32233
  C-1.44863 18.0854 -1.44863 33.9146 8.3459 43.6777C18.1404 53.4408 34.0205 53.4408 
  43.815 43.6777L61.4504 26L79.185 8.32233C88.9795 -1.44078 104.86 -1.44078 114.654 
  8.32233C124.449 18.0854 124.449 33.9146 114.654 43.6777C104.86 53.4408 88.9795 53.4408 79.185 43.6777
  L61.5073 26`;

const cssVars: ComputedRef<Record<string, string | undefined>> = computed(() => {
  return {
    '--duration': `${props.duration}s`,
  };
});
</script>

<style scoped lang="scss">
.loadingAnimation {
  position: relative;
  width: 100%;
  height: 100%;
  svg {
    overflow: visible;
    position: relative;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%)
  }
  .bubbles {
    circle {
      fill: #0085ff;
    }
    .bubble1 {
      animation: circle var(--duration) ease-in-out infinite;
    }
    .bubble2 {
      animation: circle calc(var(--duration) * 0.8) ease-in-out infinite;
    }
    .bubble3 {
      animation: circle calc(var(--duration) * 0.64) ease-in-out infinite;
    }
  }
  .bar {
    path {
      stroke-width: 6;
    }
    .barAnim {
      stroke-dasharray: 50;
      stroke-dashoffset: 0;
      animation: bar var(--duration) ease-in-out infinite;
      stroke: #0085ff;
    }
    .barBg {
      stroke: #e0e0e0;
    }
  }
  .infinity {
    stroke-width: 12;
    .infinityAnim {
      stroke-dasharray: 120 216;
      stroke-dashoffset: 0;
      animation: infinity calc(var(--duration) * 0.5) linear infinite;
      stroke: #0085ff;
    }
    .infinityBg {
      display: none;
      stroke: #e0e0e0;
    }
  }
}
@keyframes circle {
  0% {
    r: 20;
  }
  25% {
    r: 11;
  }
  50% {
    r: 20;
  }
  75% {
    r: 11;
  }
  100% {
    r: 20;
  }
}
@keyframes bar {
  0% {
    stroke-dashoffset: 49;
  }
  50% {
    stroke-dashoffset: -50;
  }
  100% {
    stroke-dashoffset: 49;
  }
}
@keyframes infinity {
  0% {
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 0 336;
    stroke-dashoffset: 160;
  }
  100% {
    stroke-dashoffset: 336;
  }
}
</style>
